import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Trash2, Plus, Minus, ShoppingBag } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { useCart } from "@/context/CartContext";
import { eur } from "@/lib/api";
import api from "@/lib/api";

export default function CartDrawer() {
  const { items, open, setOpen, updateQty, removeItem, subtotal, lineId } = useCart();
  const navigate = useNavigate();
  const [threshold, setThreshold] = useState(80);

  useEffect(() => {
    api.get("/settings").then(({ data }) => setThreshold(data.free_shipping_threshold || 80)).catch(() => {});
  }, []);

  const remaining = Math.max(0, threshold - subtotal);
  const pct = Math.min(100, (subtotal / threshold) * 100);

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent side="right" className="bg-[#0A0A0B] border-zinc-800 w-full sm:max-w-md p-0 flex flex-col" data-testid="cart-drawer">
        <SheetHeader className="p-5 border-b border-zinc-800">
          <SheetTitle className="font-display uppercase tracking-tight text-white flex items-center gap-2">
            <ShoppingBag className="w-5 h-5" /> Καλάθι ({items.length})
          </SheetTitle>
        </SheetHeader>

        {items.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center p-8">
            <ShoppingBag className="w-14 h-14 text-zinc-700 mb-4" />
            <p className="text-zinc-400 mb-6">Το καλάθι σου είναι άδειο</p>
            <button onClick={() => { setOpen(false); navigate("/shop"); }} className="btn-red px-8 py-3 text-xs font-bold tracking-widest uppercase rounded" data-testid="cart-empty-shop-btn">
              ΞΕΚΙΝΑ ΤΑ ΨΩΝΙΑ
            </button>
          </div>
        ) : (
          <>
            <div className="px-5 py-3 border-b border-zinc-800">
              {remaining > 0 ? (
                <p className="text-xs text-zinc-400 mb-2">Πρόσθεσε <span className="text-[#FF2E4C] font-semibold">{eur(remaining)}</span> για ΔΩΡΕΑΝ μεταφορικά</p>
              ) : (
                <p className="text-xs text-[#FF2E4C] font-semibold mb-2">🎉 Κέρδισες δωρεάν μεταφορικά!</p>
              )}
              <div className="h-1.5 bg-zinc-800 rounded-full overflow-hidden">
                <div className="h-full bg-[#FF2E4C] transition-all duration-500" style={{ width: `${pct}%` }} />
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-5 space-y-4">
              {items.map((it) => {
                const id = lineId(it);
                return (
                  <div key={id} className="flex gap-3" data-testid={`cart-item-${it.slug}`}>
                    <img src={it.image} alt={it.name} className="w-20 h-24 object-cover rounded bg-[#18181C]" />
                    <div className="flex-1 min-w-0">
                      <h4 className="font-display font-bold text-sm uppercase tracking-tight truncate">{it.name}</h4>
                      <p className="text-[11px] font-mono text-zinc-500 uppercase mt-0.5">{it.color} / {it.size}</p>
                      <div className="flex items-center justify-between mt-2">
                        <div className="flex items-center border border-zinc-700 rounded">
                          <button onClick={() => updateQty(id, it.quantity - 1)} className="p-1.5 hover:text-[#FF2E4C]" data-testid="cart-qty-minus"><Minus className="w-3 h-3" /></button>
                          <span className="px-2 text-sm w-7 text-center">{it.quantity}</span>
                          <button onClick={() => updateQty(id, Math.min(it.quantity + 1, it.max_stock || 99))} className="p-1.5 hover:text-[#FF2E4C]" data-testid="cart-qty-plus"><Plus className="w-3 h-3" /></button>
                        </div>
                        <span className="font-semibold text-sm">{eur(it.price * it.quantity)}</span>
                      </div>
                    </div>
                    <button onClick={() => removeItem(id)} className="text-zinc-600 hover:text-[#FF2E4C] self-start" data-testid="cart-remove-item"><Trash2 className="w-4 h-4" /></button>
                  </div>
                );
              })}
            </div>

            <div className="border-t border-zinc-800 p-5 space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-zinc-400">Υποσύνολο</span>
                <span className="font-semibold" data-testid="cart-subtotal">{eur(subtotal)}</span>
              </div>
              <button onClick={() => { setOpen(false); navigate("/checkout"); }} className="btn-red w-full py-3.5 text-sm font-bold tracking-widest uppercase rounded" data-testid="cart-checkout-btn">
                ΟΛΟΚΛΗΡΩΣΗ ΑΓΟΡΑΣ
              </button>
              <button onClick={() => { setOpen(false); navigate("/cart"); }} className="w-full py-2.5 text-xs font-bold tracking-widest uppercase text-zinc-400 hover:text-white transition-colors" data-testid="cart-view-btn">
                ΠΡΟΒΟΛΗ ΚΑΛΑΘΙΟΥ
              </button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
